// 真实分析结果 → 展示组件数据形状适配。
//
// 约定：一律从后端 insight / raw / report_markdown / leads 派生 BatchClient，
// 不再用 RAI 内存样本覆盖真实结果（见本期交付设计）。
//
// 重要：结构化 insight.leads 可能为空（线索在 report_markdown 的 O06/O07）。
// 派生逻辑优先用 insight.leads，缺失时回退解析 report_markdown。

import type { BatchClient } from './batchClients';
import { matchPercentFromStatus, type TaxPolicy } from './taxPolicies';
import { sortOpportunitiesByUrgency } from './batchClients';
import type {
  EnrichPreviewData,
  EnrichResultData,
  EnterpriseLead,
  LeadPreviewItem,
} from '@/services/enterpriseApi';

function maturityToGrade(m: string | undefined): 'A' | 'B' | 'C' | 'D' {
  const v = (m || '').toUpperCase();
  if (v === 'A') return 'A';
  if (v === 'B') return 'B';
  if (v === 'C') return 'C';
  return 'D';
}

function gradeToPriority(g: string): BatchClient['priority'] {
  if (g === 'A') return 'high';
  if (g === 'B') return 'medium';
  return 'low';
}

function compactText(value: string | undefined, fallback = '暂无数据') {
  return value && value.trim() ? value.trim() : fallback;
}

/** 剥离「（来源：…）」和「（F，来源：…）」整段；未闭合的标注删到句末。路径里常含嵌套括号。 */
function stripSourceBlocks(text: string): string {
  const s = text || '';
  const re = /[（(]\s*(?:[FIUC]\s*[,，]\s*)?来源\s*[:：]/gi;
  let out = '';
  let i = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(s)) !== null) {
    if (match.index < i) {
      re.lastIndex = i;
      continue;
    }
    out += s.slice(i, match.index);
    let depth = 0;
    let j = match.index;
    let closed = false;
    while (j < s.length) {
      const ch = s[j];
      if (ch === '（' || ch === '(') depth += 1;
      else if (ch === '）' || ch === ')') {
        depth -= 1;
        if (depth <= 0) {
          j += 1;
          closed = true;
          break;
        }
      }
      j += 1;
    }
    i = closed ? j : s.length;
    re.lastIndex = i;
  }
  out += s.slice(i);
  return out;
}

/** 与后端 _SCHEMA_LABELS 对齐。长路径在前，赋值左侧不在这里翻。 */
const SCHEMA_LABELS: Record<string, string> = {
  'relationship.investment': '对外投资',
  'relationship.shareholder': '关联股东',
  'entinfo.equitythrough': '股权穿透',
  'basic.opscope': '经营范围',
  'basic.entname': '企业名称',
  'basic.tax_type_name': '纳税人类型',
  'basic.tax_type_code': '纳税人类型',
  'basic.empnum': '从业人数',
  'basic.tags': '企业标签',
  'basic.esdate': '成立日期',
  'basic.dom': '注册地址',
  'basic.regcapcn': '注册资本',
  'basic.nic_name': '行业',
  'basic.entstatus': '登记状态',
  'basic.is_tax_rating': '纳税信用',
  'detail2.alter': '变更记录',
  'detail2.annualreport': '企业年报',
  'detail2.annualsocial': '年报社保',
  'detail2.shareholder': '股东出资',
  'detail2.abnormal': '经营异常',
  'detail2.abnormalin': '经营异常列入',
  'detail2.abnormalout': '经营异常移出',
  'detail2.annualreportcapital': '年报出资',
  'detail2.annualassets': '年报资产',
  'qualification.tax_type_name': '纳税人类型',
  'changes.altitem': '变更事项',
  'changes.altaf': '变更后',
  'changes.altbe': '变更前',
  detaillist: '明细',
  relationship: '关联关系',
  investment: '对外投资',
  shareholder: '股东',
  controller: '实际控制人',
  beneficiary: '受益所有人',
  qualification: '纳税人资格',
  tax_credit: '纳税信用',
  bidding: '招投标',
  winner: '中标记录',
  changes: '变更记录',
  detail2: '登记明细',
  basic: '工商登记',
  annualreport: '企业年报',
  annualsocial: '年报社保',
  annualassets: '年报资产',
  annualreportcapital: '年报出资',
  abnormalin: '经营异常列入',
  abnormalout: '经营异常移出',
  abnormal: '经营异常',
  frinv: '对外投资',
  person: '主要人员',
  history_manager: '历史高管',
  historyname: '曾用名',
  empnum: '从业人数',
  opscope: '经营范围',
  regcapcn: '注册资本',
  esdate: '成立日期',
  entstatus: '登记状态',
  nic_name: '行业',
  tax_type_name: '纳税人类型',
  tax_type_code: '纳税人类型',
  entname: '企业名称',
  uniscid: '统一社会信用代码',
  enttype: '企业类型',
  is_tax_rating: '纳税信用',
  ishistory: '是否历史记录',
  ancheyear: '年报年度',
  condate: '认缴出资日期',
  subcondate: '认缴出资日期',
  altitem: '变更事项',
  altbe: '变更前',
  altaf: '变更后',
  outdate: '移出日期',
  out_date: '移出日期',
  indate: '列入日期',
  in_date: '列入日期',
  tags: '企业标签',
  risk: '风险信息',
  dom: '住所',
  source_id: '',
  public_facts: '',
  data_gaps: '',
  count: '',
};

const SCHEMA_PAT = new RegExp(
  `(?<![A-Za-z0-9_])(?:${Object.keys(SCHEMA_LABELS)
    .sort((a, b) => b.length - a.length)
    .map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')})(?![A-Za-z0-9_=])`,
  'gi',
);

const QUARTER_CN = ['', '第一季度', '第二季度', '第三季度', '第四季度'];

/**
 * 与后端 sanitize_customer_text 对齐：已知字段翻成中文，其余纯字母字段整段去掉。
 * 中文事实、金额和日期保留。
 */
function sanitizeCustomerText(text: string): string {
  let s = text || '';
  s = s.replace(/(^|[；;，,、\n])\s*[FIUC]\s*[：:|｜/／—–\-]+\s*/gi, '$1');
  s = s.replace(/(?<![A-Za-z0-9_])isTaxAbnormal\s*=\s*false(?![A-Za-z0-9_])/gi, '纳税状态正常');
  s = s.replace(/(?<![A-Za-z0-9_])isTaxAbnormal\s*=\s*true(?![A-Za-z0-9_])/gi, '存在税务异常标记');
  s = s.replace(
    /(?<![A-Za-z0-9_])(?:inDate|outDate|OUTDATE|in_date|out_date)\s+(20\d{2}-\d{2}-\d{2})(?![A-Za-z0-9_])/gi,
    '$1',
  );
  s = s.replace(SCHEMA_PAT, (matched) => SCHEMA_LABELS[matched.toLowerCase()] ?? '');
  s = s.replace(/(?<![A-Za-z0-9_])Q([1-4])(?![A-Za-z0-9_])/gi, (_, n: string) => QUARTER_CN[Number(n)] || '');
  s = s.replace(/(?<![A-Za-z0-9_])so[1-5](?![A-Za-z0-9_])/gi, '');
  s = s.replace(/[A-Za-z][\w.]*\s*=\s*(?:"[^"]*"|'[^']*'|[^；;，,、（）()\s]+)?/g, '');
  s = s.replace(/[A-Za-z][\w.]*\s*为空/gi, '');
  s = s.replace(/(?<![A-Za-z0-9_])(?!E[1-4](?![A-Za-z0-9_]))[A-Za-z][A-Za-z_.]*(?![A-Za-z0-9_])/g, '');
  s = s.replace(/(?<![\dA-Za-z])\.(?![\dA-Za-z])/g, '');
  for (let i = 0; i < 6; i += 1) {
    const next = s.replace(/([\u4e00-\u9fff])\s+([\u4e00-\u9fff])/g, '$1$2');
    if (next === s) break;
    s = next;
  }
  s = s.replace(/(?:^|[；;，,、])\s*项(?=\s*["“])/g, '$1');
  s = s.replace(/(?:[；;，,、]\s*){2,}/g, '；');
  s = s.replace(/\s{2,}/g, ' ').replace(/^[；;，,、\s]+|[；;，,、\s]+$/g, '');
  const parts = s
    .split(/[；;]/)
    .map((part) => part.replace(/^[ ，,、]+|[ ，,、]+$/g, ''))
    .filter((part) => /[\u4e00-\u9fff]/.test(part));
  return parts.join('；');
}

/** 去掉 LLM 证据标注与本地路径噪声，避免前端当正文展示。 */
export function cleanEvidenceAnnotations(text: string | null | undefined): string {
  if (!text) return '';
  let s = stripSourceBlocks(text);
  s = s
    .replace(/\s*[（(]\s*[FIUC]\s*[)）]/gi, '')
    // 事实标注：F： / F—— / I——。模型有时用破折号，不是冒号。
    .replace(/(^|[；;，,、\n])\s*[FIUC]\s*[：:|｜/／—–\-]+\s*/gi, '$1')
    // Windows / Unix 绝对路径与相对资料路径。只删路径或纯文件名，不吞中文事实句。
    .replace(/[A-Za-z]:\\[^\s;；，,）)]+/g, '')
    .replace(/\/(?:Users|home|var|tmp|opt)\/[^\s;；，,）)]+/g, '')
    .replace(/(?:^|[；;\s])\d{2}\/[^\s;；，,）)]+\.(?:pdf|xlsx?|docx?|csv)/gi, '')
    .replace(/(?:^|[\s;；])[A-Za-z0-9_./\\-]{3,}\.(?:pdf|xlsx?|docx?|csv)\b/gi, '');
  s = sanitizeCustomerText(s);
  if (!/[\u4e00-\u9fff]/.test(s)) return '';
  return s;
}

const SUMMARY_PLACEHOLDER = '待补充摘要';

/** 底稿把线索叙述写在 why_now 时，公开事实字段是空的，从已保存的条目原文里取回。 */
export function narrativeFromLeadMarkdown(markdown?: string | null): string {
  if (!markdown) return '';
  const match = markdown.match(/^[ \t]*-[ \t]*why_now[ \t]*[:：][ \t]*(.+)$/m);
  return match ? cleanEvidenceAnnotations(match[1]) : '';
}

export function displayLeadSummary(input: {
  publicFacts?: string | null;
  description?: string | null;
  markdown?: string | null;
}): string {
  const usable = (value: string) => (value && value !== SUMMARY_PLACEHOLDER ? value : '');
  return (
    usable(cleanEvidenceAnnotations(input.publicFacts)) ||
    usable(cleanEvidenceAnnotations(input.description)) ||
    narrativeFromLeadMarkdown(input.markdown) ||
    ''
  );
}

const SOURCE_CODE_LABELS: Record<string, string> = {
  abnormal: '经营异常记录',
  abnormalin: '经营异常列入记录',
  abnormalout: '经营异常移出记录',
  annualreport: '企业年报',
  annualsocial: '年报社保',
  annualassets: '年报资产',
  annualreportcapital: '年报出资',
  risk: '风险扫描',
  basic: '工商登记',
  shareholder: '股东出资',
  frinv: '股东出资',
  qualification: '纳税人资格',
  tax_credit: '纳税信用',
};

/** 内部字段码（ABNORMALIN 等）不直接展示；文件名和中文来源保留。 */
export function humanizeSourceLabel(raw: string | null | undefined): string {
  const s = (raw || '').trim();
  if (!s || s === '待核验') return '公开登记数据';
  // 对端/管道技术来源不上屏
  if (/opportunity\s*service|v3\s*leads|inference|mcp/i.test(s)) {
    return '公开信息整理';
  }
  if (/[\u4e00-\u9fff]/.test(s)) return s;
  const base = s.split(/[/\\]/).pop()?.trim() || s;
  if (/\.(?:pdf|xlsx?|docx?|csv|png|jpe?g|txt|ofd)$/i.test(base)) return base;
  const key = base.replace(/\s+/g, '').toLowerCase();
  if (SOURCE_CODE_LABELS[key]) return SOURCE_CODE_LABELS[key];
  if (/^O0?\d{1,3}$/i.test(base)) return '深挖底稿';
  if (/^[A-Za-z][\w.-]*$/.test(base)) return '公开登记数据';
  return s;
}

/** 从污染字段中抽出统一社会信用代码。 */
export function extractCreditCode(text: string | null | undefined): string {
  const raw = (text || '').trim();
  if (!raw) return '待核验';
  const compact = raw.replace(/\s+/g, '');
  const m =
    compact.match(/(?:91|92|93)\d{15}[0-9A-Z]{2}/i) ||
    compact.match(/[0-9A-HJ-NP-RTUW-Y]{18}/i);
  if (m) return m[0].toUpperCase();
  const cleaned = cleanEvidenceAnnotations(raw);
  if (cleaned && cleaned.length <= 24) return cleaned;
  return '待核验';
}

/** 行业等短字段：清洗后取首句，避免路径/来源撑破卡片。 */
export function cleanShortField(
  text: string | null | undefined,
  maxLen = 40,
): string {
  const raw = (text || '').trim();
  if (/^[\d.,]+%?$/.test(raw) || /^[A-D]级$/.test(raw)) return raw;
  const cleaned = cleanEvidenceAnnotations(text);
  if (!cleaned) return '待核验';
  const first = cleaned.split(/[；;。\n]/)[0]?.trim() || cleaned;
  return first.length > maxLen ? `${first.slice(0, maxLen)}…` : first;
}

// 「优先级」文本 → 等级字母。注意先判断「最高」再判断「高」。
function priorityTextToGrade(p: string): 'A' | 'B' | 'C' | 'D' {
  const v = p || '';
  if (v.includes('最高') || v.includes('紧急')) return 'A';
  if (v.includes('高')) return 'B';
  if (v.includes('中')) return 'C';
  return 'D';
}

// 时效窗口类型（与 prompts.py O06/O07 timeliness.window 一致）
type TimeWindow = '即时' | '短期' | '中期' | '长期' | 'U' | '';

interface TimeSignal {
  window: TimeWindow;
  remainingDays: number | null;
  // 是否进入「优先/紧急跟进」：即时窗口，或剩余天数 ≤90（短期临期）
  urgent: boolean;
}

// 从结构化 lead 的时效字段推导时效信号；无时效信息时返回非紧急。
function deriveTimeSignal(lead: {
  window?: string | null;
  remaining_days?: number | null;
}): TimeSignal {
  const w = (lead.window || '') as TimeWindow;
  const rd = typeof lead.remaining_days === 'number' ? lead.remaining_days : null;
  const urgent =
    w === '即时' || rd !== null ? (w === '即时' ? true : rd !== null && rd <= 90) : false;
  return { window: w, remainingDays: rd, urgent };
}


interface ReportOpp {
  code: string;
  title: string;
  maturity: string; // 归一化后的等级字母 A/B/C/D
  service: string;
  reason: string;
}

// 从 report_markdown 的 O06/O07 段落抽取商机线索表（当结构化 leads 为空/空壳时使用）。
// 表结构在不同批次可能不同（如「成熟度/推荐服务产品」列 或「优先级/综合排序理由」列），
// 因此按表头列名动态定位，避免固定下标取错列。
function parseReportOpportunities(md: string | undefined): ReportOpp[] {
  if (!md) return [];
  const sectionRe = /##\s*O0[67][^\n]*([\s\S]*?)(?=\n##\s|\n---|\Z)/g;
  const out: ReportOpp[] = [];
  const clean = (s: string) => (s || '').replace(/\*/g, '').trim();
  let m: RegExpExecArray | null;
  while ((m = sectionRe.exec(md)) !== null) {
    const lines = m[1].split('\n').filter((l) => l.trim().startsWith('|'));
    let cols: {
      code: number;
      title: number;
      priority: number;
      maturity: number;
      service: number;
      reason: number;
    } | null = null;
    for (const line of lines) {
      if (/^\s*\|[\s:|-]+\|\s*$/.test(line)) continue; // 分隔行
      const cells = line.split('|').slice(1, -1).map((s) => s.trim());
      if (!cells.length) continue;
      const isHeader = cells.some((c) => /标题|商机名称|名称|线索编号/.test(c));
      if (isHeader) {
        cols = {
          code: cells.findIndex((c) => /编号/.test(c)),
          title: cells.findIndex((c) => /标题|商机名称|名称/.test(c)),
          priority: cells.findIndex((c) => /优先级/.test(c)),
          maturity: cells.findIndex((c) => /成熟度|等级/.test(c)),
          service: cells.findIndex((c) => /推荐服务|服务产品/.test(c)),
          reason: cells.findIndex((c) => /理由|说明|依据/.test(c)),
        };
        continue;
      }
      if (!cols) continue; // 表头之前的行忽略
      const get = (i: number) => (i >= 0 && i < cells.length ? clean(cells[i]) : '');
      const title = get(cols.title);
      const code = get(cols.code);
      if (!title) continue;
      if (code === '-' || title.includes('其余线索')) continue; // 跳过"其余"占位行
      const maturityRaw = get(cols.maturity);
      const grade = /^[A-D]$/i.test(maturityRaw)
        ? (maturityRaw.toUpperCase() as 'A' | 'B' | 'C' | 'D')
        : priorityTextToGrade(get(cols.priority));
      out.push({
        code,
        title,
        maturity: grade,
        service: get(cols.service),
        reason: get(cols.reason),
      });
    }
  }
  return out;
}

function deriveFromRealResult(result: EnrichResultData): BatchClient {
  // 单段推理的任务结果不再携带 insight（只有 full/leads/leadsPreview 等），此处兜底为空对象，
  // 避免结果页因读取 undefined 抛异常而白屏；缺失时商机回退解析 report_markdown。
  const insight = (result.insight ?? {}) as EnrichResultData['insight'];
  const raw = (result.raw ?? {}) as NonNullable<EnrichResultData['raw']>;
  const basic = (raw.basic ?? {}) as Record<string, unknown>;
  const qualification = (raw.qualification ?? {}) as Record<string, unknown>;

  // 按 lead_id 去重：priority_leads 是 leads 的子集（O07 复用 O06 的 lead_id），
  // 直接拼接会导致同一线索重复展示（与报告 build_client_report 按全量去重后的条数不一致）。
  // 去重规则：priority_leads 优先（带排序/评分），leads 补充未出现的；无 lead_id 的保留。
  const leadById = new Map<string, EnterpriseLead>();
  let inlineSeq = 0;
  for (const l of [...(insight.priority_leads ?? []), ...(insight.leads ?? [])]) {
    const key = l.lead_id ? `id:${l.lead_id}` : `inline:${inlineSeq++}`;
    if (!leadById.has(key)) leadById.set(key, l);
  }
  const structuredLeads = [...leadById.values()];
  // 真实 pipeline 常返回"空壳" leads：只有 lead_id，title/public_facts/service_products
  // 全为空。这类线索没有可展示内容，需过滤掉后再判断是否走结构化分支。
  const validLeads = structuredLeads.filter((l) => (l.title || '').trim());

  // 商机来源：优先有内容的结构化 leads；缺失时回退解析 report_markdown 的 O07 商机表。
  const reportOpps = parseReportOpportunities(result.report_markdown);

  const dq = (insight.data_quality ?? {}) as Record<string, unknown>;
  // 真实底稿 O01 的公开可用度字段为 q_score（如 "62/100（公开登记、司法…较完整；财务/申报缺失）"），
  // 见 prompts.py O01 字段要求。旧字段名（overallScore/score/dataCompleteness/completeness）已废弃，仅作兜底。
  const rawScore =
    dq.q_score ?? dq.overallScore ?? dq.score ?? dq.dataCompleteness ?? dq.completeness ?? 0;
  const dataCompleteness = (() => {
    const s = String(rawScore);
    const ratio = s.match(/(\d+(?:\.\d+)?)\s*\/\s*100/); // "62/100（…）"
    const n = ratio ? parseFloat(ratio[1]) : parseFloat(s.replace('%', ''));
    return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0;
  })();

  const opportunities: BatchClient['opportunities'] = [];

  if (validLeads.length > 0) {
    opportunities.push(
      ...validLeads.map((lead) => {
        const title = compactText(lead.title, '待补充线索');
        return {
          category: '待验证服务议题',
          title,
          description: [
            lead.public_facts ? `触发事实：${lead.public_facts}` : '',
            lead.assumptions_to_verify ? `待验证边界：${lead.assumptions_to_verify}` : '',
            lead.data_gaps?.length
              ? `建议补充资料：${lead.data_gaps.join('、')}`
              : '',
          ]
            .filter(Boolean)
            .join('；') || '暂无数据',
          estimatedValue: lead.service_products?.[0] ?? '待补充',
          urgency: (lead.maturity === 'A' || deriveTimeSignal(lead).urgent
            ? 'urgent'
            : lead.maturity === 'B'
              ? 'normal'
              : 'low') as 'urgent' | 'normal' | 'low',
          tags: [lead.evidence_level, lead.maturity, lead.lead_type].filter(Boolean) as string[],
        };
      }),
    );
  } else if (reportOpps.length > 0) {
    opportunities.push(
      ...reportOpps.map((o) => ({
        category: '待验证服务议题',
        title: o.title,
        description:
          o.reason ||
          '基于公开披露与行业特征推断，需核验内部资料后确认服务边界。',
        estimatedValue: o.service || '待验证服务',
        urgency: o.maturity === 'A' ? ('urgent' as const) : o.maturity === 'B' ? ('normal' as const) : ('low' as const),
        tags: [o.code, o.maturity].filter(Boolean),
      })),
    );
  }

  // 线索等级分布与评分：优先用结构化 leads 的真实字段（priority_score/risk_score/maturity），
  // 回退分支（报告解析）用 ReportOpp.maturity。统计 A/B/C/D 占比与 C 分均值，
  // 用于「线索等级」「商机评分」展示与「紧急跟进」判定。
  const gradeOrder = ['A', 'B', 'C', 'D'] as const;
  const gradeCount: Record<'A' | 'B' | 'C' | 'D', number> = { A: 0, B: 0, C: 0, D: 0 };
  // C 分（商业优先级 priority_score）与 M 分（成熟度 maturity_score）均为 0-100，
  // 优先用 C 分做商机评分均值；C 分缺失时回退 M 分均值；两者皆缺再回退数据完整度。
  const cScores: number[] = [];
  const mScores: number[] = [];
  // 时效维度统计：窗口分布 + 进入优先/紧急跟进的线索数（即时窗口或剩余≤90天）。
  const windowCount: Record<TimeWindow, number> = { 即时: 0, 短期: 0, 中期: 0, 长期: 0, U: 0, '': 0 };
  let timeUrgentCount = 0;

  if (validLeads.length > 0) {
    for (const l of validLeads) {
      const g = (l.maturity || '').toUpperCase();
      if (g === 'A' || g === 'B' || g === 'C' || g === 'D') gradeCount[g] += 1;
      if (typeof l.priority_score === 'number') cScores.push(l.priority_score);
      if (typeof l.maturity_score === 'number') mScores.push(l.maturity_score);
      const ts = deriveTimeSignal(l);
      windowCount[ts.window] += 1;
      if (ts.urgent) timeUrgentCount += 1;
    }
  } else {
    for (const o of reportOpps) {
      if (o.maturity === 'A' || o.maturity === 'B' || o.maturity === 'C' || o.maturity === 'D') {
        gradeCount[o.maturity] += 1;
      }
    }
  }
  // 整体等级 = 实际出现的最高等级（A > B > C > D）；无线索时为 D
  const topGrade: 'A' | 'B' | 'C' | 'D' =
    gradeOrder.find((g) => gradeCount[g] > 0) ?? 'D';

  // 商机评分 = 线索商业优先级（C 分 / priority_score）均值；
  // C 分缺失时回退成熟度（M 分 / maturity_score）均值；
  // 两者皆缺（真实底稿常不给评分字段）时回退数据完整度，并明确标注来源，避免概念混淆。
  const scoreSource: 'C分均值' | 'M分均值' | '完整度回退' = cScores.length
    ? 'C分均值'
    : mScores.length
      ? 'M分均值'
      : '完整度回退';
  const opportunityScore = cScores.length
    ? Math.round(cScores.reduce((a, b) => a + b, 0) / cScores.length)
    : mScores.length
      ? Math.round(mScores.reduce((a, b) => a + b, 0) / mScores.length)
      : dataCompleteness;

  const mainTitle = opportunities[0]?.title ?? '相关线索';

  // 资料缺口计数：结构化分支累加各线索 data_gaps；回退分支用 data_quality.缺失项 估算。
  const gapCount = validLeads.length
    ? validLeads.reduce((n, l) => n + (l.data_gaps?.length || 0), 0)
    : (() => {
        const miss = String(dq.missing_items ?? dq.缺失项 ?? '');
        const items = miss.split(/[；;、]/).filter((s) => s.trim() && !s.includes('未检索到'));
        return items.length || '待补充';
      })();

  const financialInsights: BatchClient['financialInsights'] = [
    {
      label: '商机评分',
      value: `${opportunityScore}`,
      benchmark: '线索优先级均值',
      deviation: `${scoreSource}${scoreSource === 'C分均值' || scoreSource === 'M分均值' ? `(${cScores.length || mScores.length}条)` : ''}`,
      status: opportunityScore >= 80 ? 'normal' : opportunityScore >= 60 ? 'warning' : 'normal',
    },
    {
      label: '数据完整度',
      value: `${dataCompleteness || 0}%`,
      benchmark: '公开数据可用度',
      deviation: dataCompleteness >= 70 ? '较完整' : '待补资料',
      status: (dataCompleteness || 0) >= 70 ? 'normal' : 'warning',
    },
    {
      label: '线索等级',
      value: `${topGrade}级`,
      benchmark: `A${gradeCount.A}·B${gradeCount.B}·C${gradeCount.C}·D${gradeCount.D}`,
      deviation:
        gradeCount.A > 0
          ? '有紧急商机'
          : timeUrgentCount > 0
            ? `${timeUrgentCount}条临期优先`
            : '公开模式待核验',
      status: topGrade === 'A' || timeUrgentCount > 0 ? 'warning' : topGrade === 'B' ? 'warning' : 'normal',
    },
    {
      label: '资料缺口',
      value: `${gapCount}${typeof gapCount === 'number' ? '项' : ''}`,
      benchmark: '最小资料包',
      deviation: '待补充',
      status: 'warning',
    },
    {
      label: '时效窗口',
      value:
        windowCount['即时'] + windowCount['短期'] > 0
          ? `${windowCount['即时'] + windowCount['短期']}条临期`
          : windowCount['中期'] + windowCount['长期'] > 0
            ? `${windowCount['中期'] + windowCount['长期']}条在途`
            : '暂无时效信号',
      benchmark: `即时${windowCount['即时']}·短期${windowCount['短期']}·中期${windowCount['中期']}·长期${windowCount['长期']}`,
      deviation: timeUrgentCount > 0 ? '优先跟进' : '暂无临期',
      status: timeUrgentCount > 0 ? 'warning' : 'normal',
    },
  ];

  const painPoints = opportunities[0]
    ? [
        `目前最需要先核验的是「${opportunities[0].title}」：${compactText(
          opportunities[0].description,
          '暂无数据',
        )}，现阶段只能作为待验证服务议题推进。`,
        `资料侧建议优先补齐内部账税数据。这些资料不到位时只适合做初筛和客户沟通，不适合直接形成正式财税判断。`,
      ]
    : ['当前线索需要把事件、资料和服务边界串起来看，避免只凭单点事实判断服务价值。'];

  const conversationScript: BatchClient['conversationScript'] = {
    opening: `您好，我们这次先帮贵公司做一轮财税服务机会梳理。现在看到的主要入口是「${mainTitle}」，建议先把这条线索纳入一次核验，确认边界后再判断是否进入专项服务。`,
    painPoints,
    valueProposition: `我们建议先把资料收齐后形成一份可追溯的待验证清单：哪些事项有服务价值、哪些事项资料不足、哪些事项应降级或排除。`,
    closing: `可先安排一次30分钟资料沟通，把负责人、资料清单和核验节奏定下来。`,
  };

  // 行业标签：优先取线索类型（服务方向），其次取工商行业名称；整体等级仅作辅助注记
  const industryTag =
    opportunities
      .map((o) => o.tags.find((t) => /^(优惠|风险|合规|稽查|资质|其他)/.test(t)))
      .filter(Boolean)
      .join(' · ') || compactText(basic.INDUSTRYCONAME as string, '暂无数据');

  return {
    id: raw.company_name,
    companyName: raw.company_name,
    industry: compactText((basic.INDUSTRYCONAME as string) || (basic.industry as string), '暂无数据'),
    industryTag,
    region: compactText((basic.DOM as string) || (basic.regAddress as string), '暂无数据'),
    annualRevenue: '暂无数据',
    employeeCount: 0,
    serviceType: compactText(
      ((insight.service_plans as Array<Record<string, unknown>> | undefined)?.[0]?.title as string) ||
        (qualification.taxpayerStatus as string),
      '待补充',
    ),
    priority: gradeToPriority(topGrade),
    riskLevel: topGrade,
    opportunityScore,
    // 商机线索按 紧急 > 常规 > 低优先级 排序后再展示（不影响上方基于原始首个线索推导的客户级优先级）
    opportunities: sortOpportunitiesByUrgency(opportunities),
    financialInsights,
    conversationScript,
    createdAt: new Date().toISOString().slice(0, 10),
    reportMarkdown: result.report_markdown,
  };
}

export function realResultToClient(
  result: EnrichResultData,
  _opts?: { preferApiLeads?: boolean },
): BatchClient {
  return deriveFromRealResult(result);
}

export function leadUrgency(item: {
  maturityLevel?: string | null;
  window?: string | null;
  remainingDays?: number | null;
  fallback?: 'urgent' | 'normal' | 'low' | null;
}): 'urgent' | 'normal' | 'low' {
  const window = (item.window || '').trim();
  const rem = item.remainingDays;
  // 与详情页一致：时效即时优先于成熟度字母。B 级也可以是紧急跟进。
  if (window === '即时' || (typeof rem === 'number' && rem <= 30)) return 'urgent';
  const grade = (item.maturityLevel || '').toUpperCase();
  if (grade === 'A' && !window) return 'urgent';
  // 详情跟进档只有紧急 / 较高 / 常规，没有「低优先级」。C、D 和中长期窗口都落在常规。
  return 'normal';
}

/** LeadPreviewItem[] → BatchClient.opportunities */
export function leadsPreviewToOpportunities(
  items: LeadPreviewItem[],
): BatchClient['opportunities'] {
  const list = (items || []).map((item) => {
    const hasMoney =
      !!item.estimatedValue &&
      item.estimatedValue !== '待测算' &&
      item.estimatedValue !== '待补充' &&
      !/^[A-D]级(商机|线索)?$/i.test(item.estimatedValue.trim());
    const maturity = (item.maturityLevel || '').toUpperCase();
    const maturityOk = /^[A-D]$/.test(maturity);
    // 预览角标：有金额显示金额；否则回退成熟度「B级」，避免等级丢光只剩「待测算」
    const estimatedValue = hasMoney
      ? item.estimatedValue!
      : maturityOk
        ? `${maturity}级`
        : '待测算';
    const detailHints = {
      publicFacts: item.publicFacts || undefined,
      assumptionsToVerify: item.assumptionsToVerify || undefined,
      dataGaps: item.dataGaps?.length ? item.dataGaps : undefined,
      serviceProducts: item.serviceProducts?.length ? item.serviceProducts : undefined,
      taxTypes: item.taxTypes?.length ? item.taxTypes : undefined,
      verificationAction: item.verificationAction || undefined,
      evidenceLevel: item.evidenceLevel || undefined,
      sourceIds: item.sourceIds?.length ? item.sourceIds : undefined,
      policyIds: item.policyIds?.length ? item.policyIds : undefined,
      serviceModule: item.serviceModule || undefined,
      riskScore: item.riskScore ?? null,
      priorityScore: item.priorityScore ?? null,
      maturityScore: item.maturityScore ?? null,
      window: item.window ?? null,
      deadline: item.deadline ?? null,
      remainingDays: item.remainingDays ?? null,
      policyStatus: item.policyStatus || undefined,
      humanReview: item.humanReview ?? null,
      rankReason: item.rankReason || undefined,
      nextAction: item.nextAction || undefined,
      blockingItems: item.blockingItems || undefined,
      serviceGapConfirmed: item.serviceGapConfirmed || undefined,
      detail: item.detail || undefined,
      markdown: item.markdown?.trim() || undefined,
    };
    const hasHints = Object.values(detailHints).some(
      (v) => v != null && !(Array.isArray(v) && v.length === 0),
    );
    // 成熟度字母保留进 tags，供初步线索页 maturityFromTags 使用
    const tags = [item.leadType, maturityOk ? maturity : '']
      .filter((t): t is string => !!t && !/^O?\d{2,}$/i.test(t));
    return {
      id: item.id || undefined,
      category: item.leadType || '线索',
      title: item.title || '未命名线索',
      description: compactText(
        displayLeadSummary({
          publicFacts: item.publicFacts,
          description: item.summary,
          markdown: item.markdown,
        }) || undefined,
        item.title || '待补充摘要',
      ),
      estimatedValue,
      urgency: leadUrgency(item),
      tags,
      ...(hasHints ? { detailHints } : {}),
    };
  });
  return sortOpportunitiesByUrgency(list);
}

function textOf(node: unknown): string {
  if (typeof node === 'string') return node.trim();
  if (node && typeof node === 'object' && 'text' in node) {
    const text = (node as { text?: unknown }).text;
    if (typeof text === 'string') return text.trim();
  }
  return '';
}

function strField(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return '';
}

function splitMdRow(line: string): string[] {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|')) return [];
  return trimmed
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function isSeparatorRow(cells: string[]): boolean {
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s/g, '')));
}

/** 模型有时不用 Markdown 表，而用「【列名｜列名】+ 全角竖线分行」。 */
function parseBracketTable(markdown: string): Record<string, string>[] {
  const headerMatch = markdown.match(/【([^】]+)】/);
  if (!headerMatch || headerMatch.index == null) return [];
  const header = headerMatch[1]
    .split(/[｜|]/)
    .map((cell) => cell.trim())
    .filter(Boolean);
  if (header.length < 2) return [];
  const body = markdown.slice(headerMatch.index + headerMatch[0].length);
  const rows: Record<string, string>[] = [];
  for (const line of body.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const cells = trimmed.split('｜').map((cell) => cell.trim());
    if (cells.length < 2) continue;
    const row: Record<string, string> = {};
    header.forEach((name, index) => {
      row[name] = cells[index] || '';
    });
    rows.push(row);
  }
  return rows;
}

function parseMdTable(markdown: string): Record<string, string>[] {
  const lines = markdown
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.startsWith('|'));
  if (lines.length >= 2) {
    const header = splitMdRow(lines[0]);
    const rows: Record<string, string>[] = [];
    for (const line of lines.slice(1)) {
      const cells = splitMdRow(line);
      if (!cells.length || isSeparatorRow(cells)) continue;
      const row: Record<string, string> = {};
      header.forEach((name, index) => {
        if (name) row[name] = cells[index] || '';
      });
      rows.push(row);
    }
    if (rows.length > 0) return rows;
  }
  return parseBracketTable(markdown);
}

function sectionMarkdown(markdown: string, heading: string): string {
  const match = markdown.match(new RegExp(`##\\s*${heading}[\\s\\S]*?(?=\\n##\\s|$)`));
  return match ? match[0] : '';
}

/** preview.profile.tax_types.text 里「常规税种」表的税种列。 */
function parseRoutineTaxTypes(profile: Record<string, unknown>): string[] {
  const markdown = textOf(profile.tax_types);
  const section = sectionMarkdown(markdown, '常规税种') || markdown;
  const names = parseMdTable(section)
    .map((row) => row['税种'] || '')
    .map((name) => name.replace(/\*\*/g, '').trim())
    .filter((name) => name && name !== '税种');
  return [...new Set(names)];
}

/** preview.profile.policies.text 政策表 → 画像/政策页可用结构。 */
function parseProfilePolicies(profile: Record<string, unknown>): TaxPolicy[] {
  const rows = parseMdTable(textOf(profile.policies));
  return rows.flatMap((row, index) => {
      const title = (row['政策名称'] || '').replace(/\*\*/g, '').trim();
      if (!title || title === '政策名称') return [];
      const status = (row['匹配状态'] || '').trim();
      const matchScore = matchPercentFromStatus(status);
      if (matchScore == null) return [];
      const conditions = (row['适用条件'] || '')
        .split(/[；;]/)
        .map((item) => item.trim())
        .filter(Boolean);
      const link = (row['官方链接'] || '').trim();
      const keyPoints = link ? [...conditions, `官方链接：${link}`] : conditions;
      return [{
        id: `preview-policy-${index + 1}`,
        title,
        documentNumber: row['文号'] || '—',
        category: row['政策类别'] || '政策',
        issuingAuthority: row['发文机关'] || '—',
        publishDate: row['发布日期'] || '—',
        effectiveDate: row['有效期'] || '—',
        status,
        matchScore,
        matchReason: row['与企业的匹配依据'] || '待核实',
        summary: row['核心内容'] || '—',
        keyPoints: keyPoints.length > 0 ? keyPoints : ['详见政策正文'],
        impact: row['潜在影响'] || '待核实',
      } satisfies TaxPolicy];
    });
}

/** 浅分析 preview 响应 → BatchClient（不依赖 insight 全量）。优先 v3 leads.active。 */
export function enrichPreviewToClient(
  companyName: string,
  data: EnrichPreviewData,
): BatchClient {
  const fromV3Preview =
    data.leads?.active?.length
      ? data.leads.active.map((item) => ({
          id: String(item.lead_id || item.id || ''),
          leadType: String(item.lead_type || '其他'),
          title: String(item.title || '未命名线索'),
          estimatedValue: '待测算',
          summary: Array.isArray(item.inferences)
            ? item.inferences.map(String).join('；')
            : null,
          maturityLevel: (String(item.maturity || '')
            .trim()
            .toUpperCase()
            .slice(0, 1) || null) as LeadPreviewItem['maturityLevel'],
          publicFacts: Array.isArray(item.public_facts)
            ? item.public_facts.map(String).join('；')
            : null,
          dataGaps: Array.isArray(item.unknowns)
            ? item.unknowns.map(String)
            : undefined,
          taxTypes: Array.isArray(item.tax_types)
            ? item.tax_types.map(String)
            : undefined,
          serviceProducts: Array.isArray(item.service_products)
            ? item.service_products.map(String)
            : undefined,
        }))
      : [];
  const opportunities = leadsPreviewToOpportunities(
    fromV3Preview.length ? fromV3Preview : data.leadsPreview || [],
  );
  const profile =
    data.preview && typeof data.preview === 'object'
      ? ((data.preview as { profile?: Record<string, unknown> }).profile ?? {})
      : {};
  const intro =
    typeof profile.company_intro === 'string' ? profile.company_intro.trim() : '';
  const chain =
    typeof profile.industry_chain === 'string' ? profile.industry_chain.trim() : '';

  const raw = (data.raw && typeof data.raw === 'object' ? data.raw : {}) as Record<
    string,
    unknown
  >;
  const basic =
    raw.basic && typeof raw.basic === 'object'
      ? (raw.basic as Record<string, unknown>)
      : {};
  const qualification =
    raw.qualification && typeof raw.qualification === 'object'
      ? (raw.qualification as Record<string, unknown>)
      : {};

  const nicName = strField(basic, 'nic_name');
  const nicTop = strField(basic, 'nic_top_name');
  const industryFromBasic = strField(basic, 'industry') || nicTop || nicName;
  const regCapital = strField(basic, 'regCapital', 'REGCAPCN', 'REGCAP');
  const establishDate =
    strField(basic, 'establishDate', 'ESDATE', 'establishedAt') ||
    (data.company?.establishDate || '').trim();
  const staffSize =
    strField(basic, 'staffSize', 'EMPNUM', 'staff_size') ||
    (data.company?.staffSize || '').trim();
  const staffSizeNumMatch = staffSize.match(/(\d[\d,]*)/);
  const staffSizeNum = staffSizeNumMatch
    ? Number(staffSizeNumMatch[1].replace(/,/g, ''))
    : 0;
  const taxpayerType =
    strField(basic, 'tax_type_name', 'taxpayerType') ||
    strField(qualification, 'tax_type_name', 'taxpayerType') ||
    '一般纳税人';
  const routineTaxTypes = parseRoutineTaxTypes(profile);
  const relatedPolicies = parseProfilePolicies(profile);

  const industry = compactText(
    data.company?.industry && data.company.industry !== '—'
      ? data.company.industry
      : industryFromBasic || undefined,
    '待补全',
  );
  const industryLeaf = nicName.split('-').pop()?.trim() || '';
  const industryTag =
    industryLeaf && industryLeaf !== industry ? industryLeaf : '';

  const fallbackParts = [
    establishDate ? `成立于${establishDate}` : '',
    regCapital ? `注册资本${regCapital}` : '',
    industryFromBasic ? `所属行业${industryFromBasic}` : '',
    staffSize ? `人员规模${staffSize}` : '',
  ].filter(Boolean);
  const businessDescription =
    (data.profile?.companyIntro || '').trim() ||
    intro ||
    (fallbackParts.length > 0
      ? `${companyName}${fallbackParts.length ? `，${fallbackParts.join('，')}` : ''}。`
      : `${companyName}的公开数据浅分析已完成，上传明细后可继续深度挖掘。`);

  const topMaturity = (
    fromV3Preview.length ? fromV3Preview : data.leadsPreview || []
  )
    .map((l) => (l.maturityLevel || '').toUpperCase())
    .find((g) => g === 'A' || g === 'B' || g === 'C' || g === 'D') as
    | 'A'
    | 'B'
    | 'C'
    | 'D'
    | undefined;
  const riskLevel = topMaturity ?? 'D';
  const mainTitle = opportunities[0]?.title ?? '初步线索';

  return {
    id: companyName,
    companyName,
    industry,
    industryTag,
    region: compactText(
      (data.company?.region && data.company.region !== '—'
        ? data.company.region
        : strField(basic, 'region_name', 'regLocation', 'DOM')) || undefined,
      '待补全',
    ),
    annualRevenue: compactText(
      regCapital ? (regCapital.includes('万') ? regCapital : `注册资本 ${regCapital}`) : undefined,
      '待补全',
    ),
    employeeCount: staffSizeNum || 0,
    establishDate: establishDate || undefined,
    staffSizeLabel: staffSize || undefined,
    serviceType: '初步线索预览',
    priority: gradeToPriority(riskLevel),
    riskLevel,
    opportunityScore: Math.min(100, Math.max(40, Math.max(opportunities.length, 1) * 12)),
    businessDescription,
    taxProfile: {
      taxpayerType,
      taxTypes: routineTaxTypes.length > 0 ? routineTaxTypes : ['增值税', '企业所得税'],
      industryPosition: chain || industryFromBasic || industry || '待补全',
    },
    relatedPolicies,
    opportunityPortrait: {
      primary: mainTitle,
      secondary: opportunities.slice(1, 4).map((o) => o.title),
      reason: '基于公开数据与推理初步线索生成，上传明细后可深度补正。',
    },
    opportunities,
    financialInsights: [
      {
        label: '初步线索数',
        value: `${opportunities.length}`,
        benchmark: '公开数据',
        deviation: opportunities.length > 0 ? '已产出' : '待补全',
        status: opportunities.length > 0 ? 'normal' : 'warning',
      },
      {
        label: '分析阶段',
        value: '浅分析',
        benchmark: '可继续',
        deviation: '深度挖掘',
        status: 'normal',
      },
    ],
    conversationScript: {
      opening: `您好，我们已完成「${companyName}」的初步线索梳理，当前共 ${opportunities.length} 条待验证议题。`,
      painPoints: [
        opportunities[0]
          ? `优先核验「${opportunities[0].title}」：${opportunities[0].description}`
          : '当前无线索，建议补充企业资料后重试。',
        '上传发票/申报等明细后，可启动深度挖掘补正线索成熟度。',
      ],
      valueProposition: '建议先确认初步线索边界，再决定是否进入深度挖掘与专项服务。',
      closing: '可先保存本页结果，准备好内部资料后继续深度挖掘。',
    },
    createdAt: new Date().toISOString().slice(0, 10),
    reportMarkdown: '',
  };
}

/** 用深度 leadsPreview 覆盖商机列表，保留画像/话术骨架。 */
export function mergeDeepLeads(
  client: BatchClient,
  deepLeads: LeadPreviewItem[],
): BatchClient {
  const opportunities = leadsPreviewToOpportunities(deepLeads);
  if (opportunities.length === 0) {
    return { ...client };
  }
  const top = opportunities[0];
  const topMaturity = (deepLeads[0]?.maturityLevel || '').toUpperCase();
  const riskLevel =
    topMaturity === 'A' || topMaturity === 'B' || topMaturity === 'C' || topMaturity === 'D'
      ? topMaturity
      : client.riskLevel;
  return {
    ...client,
    priority: gradeToPriority(riskLevel),
    riskLevel,
    opportunityScore: Math.min(100, Math.max(client.opportunityScore, opportunities.length * 14)),
    opportunities,
    opportunityPortrait: {
      primary: top.title,
      secondary: opportunities.slice(1, 4).map((o) => o.title),
      reason: '深度挖掘已结合上传资料补正线索列表。',
    },
    financialInsights: [
      {
        label: '深度线索数',
        value: `${opportunities.length}`,
        benchmark: 'deep leadsPreview',
        deviation: '已完成深挖',
        status: 'good',
      },
      ...client.financialInsights.filter((f) => f.label !== '初步线索数' && f.label !== '深度线索数'),
    ],
  };
}
